"use strict";
/**
 * @file Computes the distance between two polygons
 *
 * This file deals with measuring the distance between two polygons
 * It would be very nice if Turf offered a way to do this, but it's functions
 * are implemented naively - eg: checking every point vs every other point.
 * As a result when running it on big polygons it can be very slow.
 * To overcome this, the implementations here are accelerated using an rtree.
 * One of our projects contains a polygon of 63000 vertices. Using turf's functions
 * takes >20mins to run. The implementation here does it in <20 seconds.
 * The sacrifice is a slight loss of accuracy as it does not operate in a spherical
 * coordinate system when measuring distance.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getClosestDistanceBetweenPolygons = exports.getNearestPointOnLine = exports.distanceBetweenSquaredScalingFactors = exports.distanceBetweenSquared = void 0;
const memoize_weak_1 = __importDefault(require("memoize-weak"));
const rbush_1 = __importDefault(require("rbush"));
const unit_conversion_1 = require("./unit-conversion");
/**
 * Simple distance implementation in cartesian space.
 *
 * @param point1 Measure from here
 * @param point2 Measure to here
 * @returns The distance squared
 */
function distanceBetweenSquared(point1, point2) {
    return (point1[0] - point2[0]) ** 2 + (point1[1] - point2[1]) ** 2;
}
exports.distanceBetweenSquared = distanceBetweenSquared;
/**
 * Distance implementation in cartesian space. This function
 * gets called LOTS so it needs to be as fast as possible.
 * This is why we don't (eg) operate in a spherical coordinate system
 * or use turf's distance function.
 * However there is one nod to earths roundness: the `factors` parameter
 * allows scaling the X and Y "delta's" - eg to accommodate for the compression
 * of longitude lines near the poles
 *
 *
 * @param point1 Measure from here
 * @param point2 Measure to here
 * @param factors Scale the X and Y deltas.
 * @returns The distance squared
 */
function distanceBetweenSquaredScalingFactors(point1, point2, factors) {
    return ((point1[0] - point2[0]) * factors[0]) ** 2 + ((point1[1] - point2[1]) * factors[1]) ** 2;
}
exports.distanceBetweenSquaredScalingFactors = distanceBetweenSquaredScalingFactors;
/**
 * Returns the closest point between a point and a line segment. This gets called
 * LOTS so it needs to be as fast as possible.
 * As a result, this function does NOT look at the compression caused by lines of longitude
 * converging at the poles.
 *
 * @param point Measure from this point
 * @param start Start point of the line
 * @param end End point of the line
 * @returns The position on the line that is the closest to `point`
 */
function getNearestPointOnLine(point, start, end) {
    const lineLength = distanceBetweenSquared(start, end);
    if (lineLength === 0) {
        return start;
    }
    const dx = end[0] - start[0];
    const dy = end[1] - start[1];
    const percentAlongSegment = ((point[0] - start[0]) * dx + (point[1] - start[1]) * dy) / lineLength;
    const clampedPercent = Math.max(0, Math.min(1, percentAlongSegment));
    return [start[0] + clampedPercent * dx, start[1] + clampedPercent * dy];
}
exports.getNearestPointOnLine = getNearestPointOnLine;
/**
 * Build an acceleration structure for evaluating nearby line segments.
 *
 * @param feature Compute the acceleration structure for this polygon
 * @returns A tree that can be queried for each line in the feature
 */
function generateLineTree(feature) {
    const tree = new rbush_1.default();
    const polyRings = feature.geometry.coordinates;
    const treeSegments = [];
    for (let polyRingIndex = 0; polyRingIndex < polyRings.length; polyRingIndex += 1) {
        const pointArray = polyRings[polyRingIndex];
        for (let startVertIndex = 0; startVertIndex < pointArray.length - 1; startVertIndex += 1) {
            const endVertIndex = startVertIndex + 1;
            const pointHere = pointArray[startVertIndex];
            const pointNext = pointArray[endVertIndex];
            treeSegments.push({
                minX: Math.min(pointHere[0], pointNext[0]),
                minY: Math.min(pointHere[1], pointNext[1]),
                maxX: Math.max(pointHere[0], pointNext[0]),
                maxY: Math.max(pointHere[1], pointNext[1]),
                startPosition: pointHere,
                endPosition: pointNext,
                startVertIndex: startVertIndex,
                endVertIndex: endVertIndex,
                ringIndex: polyRingIndex,
            });
        }
    }
    tree.load(treeSegments);
    return tree;
}
const getLineTree = (0, memoize_weak_1.default)(generateLineTree);
/**
 * Computes the minimum distance between every point in one feature to
 * every line segment in another feature.
 * Uses an acceleration structure to complete this in < O(n2) time.
 * In theory it will average O(n*log_m(n))
 *
 * The acceleration structure imposes the restriction that there is a
 * maximum distance it will search. This is exposed in the `boundInflate`
 * parameter.
 *
 * @param pointFeature Iterate through all points in this feature
 * @param ringFeature And compare it to all lines in this feature
 * @param maxEdgeLength If the polygons are closer than this, the returned distance will be correct. If the
 * polygons are further apart, the distance returned may be infinity.
 * @param measurementUnit Allows specifying the `maxEdgeLength` in degrees or meters. Similarly
 * alters the "distance" field in the output data
 * @returns Data about the closest location. If no closest distance is found,
 * the output distance of this object will be infinity.
 */
function minDistancePointFeatureToRingFeature(pointFeature, ringFeature, maxEdgeLength, measurementUnit) {
    let closestLocation = {
        distance: Infinity,
        ringIndexA: 0,
        vertexIndexA: 0,
        positionA: [0, 0],
        ringIndexB: 0,
        vertexIndexB: 0,
        positionB: [0, 0],
    };
    const lineTree = getLineTree(ringFeature);
    let degreesToUnitFactors = [1.0, 1.0];
    if (measurementUnit === 'meters') {
        degreesToUnitFactors = (0, unit_conversion_1.degreesToMetresFactor)(ringFeature.geometry.coordinates[0][0]);
    }
    const boundInflateX = maxEdgeLength / degreesToUnitFactors[0];
    const boundInflateY = maxEdgeLength / degreesToUnitFactors[1];
    for (let ringIndex = 0; ringIndex < pointFeature.geometry.coordinates.length; ringIndex += 1) {
        const pointArray = pointFeature.geometry.coordinates[ringIndex];
        for (let pointIndex = 0; pointIndex < pointArray.length; pointIndex += 1) {
            // This loop through all of the one features verts is O(n)
            if (pointIndex === 0 || ringIndex === 0) {
                // Check we are doing interior -> exterior or exterior -> exterior comparisons
                const point = pointArray[pointIndex];
                const searchArea = {
                    minX: point[0] - boundInflateX,
                    minY: point[1] - boundInflateY,
                    maxX: point[0] + boundInflateX,
                    maxY: point[1] + boundInflateY,
                };
                // But rather than iterate through all of the other features verts, this query averages
                // O(log_m(n)) time.
                const candidateSegments = lineTree.search(searchArea);
                for (let j = 0; j < candidateSegments.length; j += 1) {
                    const segment = candidateSegments[j];
                    const nearestPoint = getNearestPointOnLine(point, segment.startPosition, segment.endPosition);
                    const distance = distanceBetweenSquaredScalingFactors(point, nearestPoint, degreesToUnitFactors) ** 0.5;
                    if (distance < closestLocation.distance) {
                        closestLocation = {
                            distance: distance,
                            ringIndexA: ringIndex,
                            vertexIndexA: pointIndex,
                            positionA: point,
                            ringIndexB: segment.ringIndex,
                            vertexIndexB: segment.startVertIndex,
                            positionB: nearestPoint,
                        };
                    }
                }
            }
        }
    }
    return closestLocation;
}
/**
 * Calculates the minimum distance between two polygons. This is a reasonably expensive function
 *
 * This function works for when a polygon contains multiple loops but does not check
 * for intersections.
 *
 * @param featureA One polygon to measure the distance from
 * @param featureB The other polygon to measure the distance from
 * @param maxEdgeLength If the polygons are closer than this, the returned distance will be correct. If the
 * polygons are further apart, the distance returned may be infinity. The unit is specified in
 * measurementUnit
 * @param measurementUnit Allows specifying the `maxEdgeLength` in degrees or meters.
 * @returns Information about the closest distance between polygons
 */
function getClosestDistanceBetweenPolygons(featureA, featureB, maxEdgeLength, measurementUnit) {
    // Point to line distance checking is not symmetric. Consider:
    //                   +
    //                   |
    //                   +
    //
    //   +-------------------------------------+
    //
    // So we have to check both directions
    const closestA2B = minDistancePointFeatureToRingFeature(featureA, featureB, maxEdgeLength, measurementUnit);
    const closestB2A = minDistancePointFeatureToRingFeature(featureB, featureA, maxEdgeLength, measurementUnit);
    const closest = closestA2B;
    if (closestA2B.distance > closestB2A.distance) {
        closest.distance = closestB2A.distance;
        // Also need to flip the indexes
        closest.ringIndexA = closestB2A.ringIndexB;
        closest.vertexIndexA = closestB2A.vertexIndexB;
        closest.positionA = closestB2A.positionB;
        closest.ringIndexB = closestB2A.ringIndexA;
        closest.vertexIndexB = closestB2A.vertexIndexA;
        closest.positionB = closestB2A.positionA;
    }
    return closest;
}
exports.getClosestDistanceBetweenPolygons = getClosestDistanceBetweenPolygons;
